/**
 * The United Kingdom charity regulator a registration number belongs to. These are the
 * camelCase strings the API serialises `compliance.charityRegulator` as.
 */
export type CharityRegulator = 'charityCommission' | 'oscr' | 'ccni';

/**
 * The three regulator number formats, in the order the API checks them. They never
 * overlap, so the regulator can be inferred from the number alone.
 *
 * Mirrors ValidationHelpers.GetUkCharityRegulator in the core API. Letters are written as
 * character classes rather than relying on an ignore-case flag, because validationHelper
 * builds its regex with `new RegExp(pattern)` and sets no flags. The API upper-cases
 * before it validates, so accepting either case here keeps the two in step.
 */
const regulatorFormats: { regulator: CharityRegulator; pattern: string }[] = [
  // England and Wales: 6 or 7 digits, e.g. 1234567.
  { regulator: 'charityCommission', pattern: '\\d{6,7}' },
  // Scotland (OSCR): SC followed by 6 digits, e.g. SC012345.
  { regulator: 'oscr', pattern: '[Ss][Cc]\\d{6}' },
  // Northern Ireland (CCNI): NIC followed by 6 digits, e.g. NIC123456.
  { regulator: 'ccni', pattern: '[Nn][Ii][Cc]\\d{6}' }
];

/**
 * Pattern for the validation model. Surrounding whitespace is tolerated because the API
 * trims before it validates, so rejecting it here would be stricter than the server.
 */
export const CHARITY_REGULATOR_NUMBER_PATTERN = `^\\s*(?:${regulatorFormats
  .map((format) => format.pattern)
  .join('|')})\\s*$`;

/**
 * Normalises a registration number for comparison, matching how the API stores it.
 * Returns null when nothing meaningful was supplied.
 */
export const normalizeCharityRegulatorNumber = (
  value?: string | null
): string | null => {
  return value && value.trim() ? value.trim().toUpperCase() : null;
};

/**
 * Infers the regulator a registration number belongs to from its format, or null when
 * the value is blank or matches none of the three formats.
 */
export const getCharityRegulator = (
  value?: string | null
): CharityRegulator | null => {
  const normalized = normalizeCharityRegulatorNumber(value);
  if (!normalized) {
    return null;
  }

  const match = regulatorFormats.find((format) =>
    new RegExp(`^${format.pattern}$`).test(normalized)
  );

  return match ? match.regulator : null;
};

/**
 * The United Kingdom HMRC charity reference: two letters (or a single X) followed by up
 * to 5 digits, e.g. XR12345. Only needed to claim Gift Aid, so it is optional.
 */
export const HMRC_CHARITY_REFERENCE_PATTERN = '^([A-Za-z]{2}|[Xx])\\d{1,5}$';

/**
 * Validation model for the HMRC charity reference. There is no `required` rule:
 * isPatternValid short-circuits on a blank value, which is what keeps it optional.
 */
export const hmrcCharityReferenceValidationModel = {
  pattern: HMRC_CHARITY_REFERENCE_PATTERN,
  errorMessegeKey: 'errors.validation.hmrcCharityReference'
};
